import React, {Suspense, useEffect, useRef, useState} from 'react';
import {BrowserRouter, Navigate, Route, Routes} from 'react-router-dom';
import Welcome from "./components/Welcome";
import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  createTheme,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Switch,
  SxProps,
  ThemeProvider,
  Toolbar,
  Typography, useTheme
} from '@mui/material';
import {DarkModeRounded, LightModeRounded, LogoutRounded, MenuRounded} from "@mui/icons-material";
import {ProductPage} from "./pages/ProductPage";
import CustomerPage from "./pages/CustomerPage";
import {AddProductPage} from "./pages/AddProductPage";
import UpdateCustomerPage from "./pages/UpdateCustomerPage";
import ConfigPage from "./pages/ConfigPage";
import {Theme} from "@mui/material/styles";
import KeycloakService from "./KeycloakService";
import {useTranslation} from "react-i18next";
import {SalesPersonPage} from "./pages/SalesPersonPage";
import {UpdateSalesPersonPage} from "./pages/UpdateSalesPersonPage";
import AuditLogPage from "./pages/AuditLogPage";
import BillingPage from "./pages/BillingPage";
import ErrorBoundary from "./components/ErrorBoundary";
import ReportPage from "./pages/ReportPage";
import {UserRoleProvider, useUserRole} from "./utils/UserRoleContext";
import ViewerCustomerPage from "./pages/ViewerCustomerPage";
import {ViewerProductPage} from "./pages/ViewerProductPage";

const ManualPage = React.lazy(() => import("./pages/ManualPage"));

function buildTheme(darkMode: boolean) {
  return createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: { main: '#008E7F' },
      secondary: { main: '#45D8C3' },
      error: { main: '#EF7B45' },
      ...(!darkMode && {
        text: { primary: '#494547', secondary: '#9D9D9D' },
        background: { default: '#F8F8F8', paper: '#FFFFFF' },
      }),
      action: {
        disabledBackground: '#ECECEC',
      },
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          colorPrimary: {
            backgroundColor: darkMode ? '#1E1E1E' : '#ECECEC',
          },
        },
      },

      MuiButton: {
        styleOverrides: {
          root: {
            color: darkMode ? '#FFFFFF' : '#494547',
          },
          contained: {
            color: '#FFFFFF',
            backgroundColor: darkMode ? '#008E7F' : undefined,
            '&:hover': {
              backgroundColor: darkMode ? '#006f65' : undefined,
            },
            '&.Mui-disabled': {
              backgroundColor: darkMode ? '#333333' : '#ECECEC',
              color: darkMode ? '#666666' : '#9D9D9D',
            },
          },
        },
        // MUI v7 removed the per-color-variant style-override slots (e.g. `containedPrimary`,
        // `containedSecondary`) from ButtonClasses. The `variants` API is the current
        // equivalent for styling a specific variant+color combination.
        variants: [
          {
            props: { variant: 'contained', color: 'primary' },
            style: {
              backgroundColor: '#008E7F',
              color: '#FFFFFF',
              '&:hover': { backgroundColor: '#006f65' },
            },
          },
          {
            props: { variant: 'contained', color: 'secondary' },
            style: {
              backgroundColor: '#EF7B45',
              color: '#FFFFFF',
              '&:hover': { backgroundColor: '#d66738' },
            },
          },
        ],
      },

      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: darkMode ? '#2A2A2A' : '#ECECEC',
          },
        },
      },

      MuiInputBase: {
        styleOverrides: {
          input: {
            color: darkMode ? '#FFFFFF' : '#494547',
            '&:-webkit-autofill': {
              WebkitBoxShadow: `0 0 0 100px ${darkMode ? '#2A2A2A' : '#FFFFFF'} inset`,
              WebkitTextFillColor: darkMode ? '#FFFFFF' : '#494547',
            },
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: darkMode ? '#2A2A2A' : '#FFFFFF',
            '&:hover': {
              backgroundColor: darkMode ? '#333333' : '#FFFFFF',
            },
          },
        },
      },

      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: darkMode ? '#2A2A2A' : '#F0F0F0',
          },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: darkMode ? '#1E1E1E' : '#FFFFFF',
            color: darkMode ? '#FFFFFF' : '#494547',
          },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          root: {
            color: darkMode ? '#FFFFFF' : '#494547',
            borderBottomColor: darkMode ? '#333333' : undefined,
          },
          head: {
            backgroundColor: darkMode ? '#2A2A2A' : undefined,
            color: darkMode ? '#FFFFFF' : '#494547',
          },
        },
      },

      MuiSelect: {
        styleOverrides: {
          select: {
            backgroundColor: darkMode ? '#2A2A2A' : '#FFFFFF',
            color: darkMode ? '#FFFFFF' : '#494547',
          },
          icon: {
            color: darkMode ? '#FFFFFF' : '#494547',
          },
        },
      },

      MuiMenuItem: {
        styleOverrides: {
          root: {
            backgroundColor: darkMode ? '#1E1E1E' : '#FFFFFF',
            color: darkMode ? '#FFFFFF' : '#494547',
            '&:hover': {
              backgroundColor: darkMode ? '#2A2A2A' : undefined,
            },
            '&.Mui-selected': {
              backgroundColor: darkMode ? '#004d45' : undefined,
            },
          },
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: darkMode ? '#1E1E1E' : '#FFFFFF',
            color: darkMode ? '#FFFFFF' : '#494547',
          },
        },
      },

      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: darkMode ? '#333333' : undefined,
          },
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: {
            color: darkMode ? '#FFFFFF' : '#494547',
          },
        },
      },

      MuiCssBaseline: {
        styleOverrides: {
          '*': {
            scrollbarWidth: 'thin',
            scrollbarColor: darkMode ? '#555555 #1E1E1E' : '#BBBBBB #F8F8F8',
          },
          '*::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '*::-webkit-scrollbar-track': {
            backgroundColor: darkMode ? '#1E1E1E' : '#F8F8F8',
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: darkMode ? '#555555' : '#BBBBBB',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: darkMode ? '#777777' : '#999999',
            },
          },
        },
      },
    },
  });
}

export default function App() {
  const [darkMode, setDarkMode] = useState<boolean>(
    localStorage.getItem('darkMode') === 'true'
  );

  const theme = React.useMemo(() => buildTheme(darkMode), [darkMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline/>
      <UserRoleProvider>
        <BrowserRouter>
          <AppContent darkMode={darkMode} setDarkMode={setDarkMode}/>
        </BrowserRouter>
      </UserRoleProvider>
    </ThemeProvider>
  );
}

function AppContent({darkMode, setDarkMode}: {
  darkMode: boolean;
  setDarkMode: (val: boolean) => void;
}) {
  const {t, i18n} = useTranslation();
  const {isAdmin, isSuperAdmin, isViewer} = useUserRole();
  const isOnlyViewer = isViewer && !isAdmin;
  const theme = useTheme();

  const topNavItems: {key: string; path: string}[] = isAdmin
    ? [
      {key: 'customer', path: '/customers'},
      {key: 'products', path: '/products/inCareNetHF'},
    ]
    : isViewer
      ? [
        {key: 'customer', path: '/view/customers'},
        {key: 'products', path: '/view/products/inCareNetHF'},
      ]
      : [];

  const sidebarNavItems: {key: string; path: string}[] = [
    ...(isAdmin ? [{key: 'reporting', path: '/report'}] : []),
    ...(isSuperAdmin ? [{key: 'billing', path: '/billing'}] : []),
    ...(isAdmin ? [{key: 'salesperson', path: '/salesperson'}] : []),
    ...(isAdmin ? [{key: 'auditlog', path: '/auditlog'}] : []),
    {key: 'manual', path: '/manual'},
    ...(isAdmin ? [{key: 'configuration', path: '/configuration'}] : []),
  ];

  function correctButtonSX(path: string): SxProps<Theme> {
    const isCurrentPage = window.location.pathname.toLowerCase().includes(path.toLowerCase());
    return {
      color: isCurrentPage ? theme.palette.error.main : theme.palette.text.primary
    }
  }

  function isCurrentPath(path: string): boolean {
    return window.location.pathname.toLowerCase().includes(path.toLowerCase());
  }

  const [isEnglish, setIsEnglish] = useState(i18n.language.startsWith('en'));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const drawerPaperRef = useRef<HTMLDivElement | null>(null);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!sidebarOpen) return;
    function handleDocumentClick(e: MouseEvent) {
      const target = e.target as Node | null;
      if (!target) return;
      if (drawerPaperRef.current?.contains(target)) return;
      if (menuButtonRef.current?.contains(target)) return;
      setSidebarOpen(false);
    }
    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, [sidebarOpen]);

  async function changeLanguage(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.checked;
    setIsEnglish(val);
    await i18n.changeLanguage(val ? 'en' : 'de');
  }

  function toggleDarkMode(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.checked;
    setDarkMode(val);
    localStorage.setItem('darkMode', String(val));
    document.body.style.backgroundColor = val ? '#121212' : '#F8F8F8';
  }

  const isLoggedIn = KeycloakService.instance.isLoggedIn();

  return (
    <>
      <AppBar position={'static'}>
        <Toolbar>
          {isLoggedIn && (
            <IconButton
              ref={menuButtonRef}
              size="large"
              edge="start"
              aria-label={t('appBar.menu', 'Menu')}
              onClick={() => setSidebarOpen((open) => !open)}
              sx={{mr: 1}}
            >
              <MenuRounded/>
            </IconButton>
          )}

          <Box sx={{mr: 3}}>
            <Typography
              variant="h5"
              component="a"
              href="/"
              color={'error'}
              sx={{
                display: {xs: 'none', md: 'flex'},
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 920,
                letterSpacing: '.1rem',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              LICENSE MANAGER
            </Typography>
          </Box>

          {isLoggedIn ?
            <Stack direction={'row'} sx={{width: '100%', justifyContent: 'space-between', alignItems: 'center'}}>
              <Stack direction={'row'} sx={{justifyContent: 'left', alignItems: 'center', flexWrap: 'wrap'}}>
                {topNavItems.map((item) => (
                  <Button href={item.path}
                          variant={'text'}
                          size={'large'}
                          sx={{...correctButtonSX(item.path), mx: .5}}
                          key={item.key}
                          color={"primary"}>
                    {t(`appBar.${item.key}`)}
                  </Button>
                ))}
              </Stack>

              <Stack direction={'row'} sx={{justifyContent: 'end', alignItems: 'center', gap: 1}}>
                {/* Language switch */}
                <div style={{marginTop: '11px'}}>
                  <Typography sx={{color: darkMode ? 'white' : 'black'}}>{t('languages.de')}</Typography>
                </div>
                <div style={{marginTop: '4px'}}>
                  <Switch
                    checked={isEnglish}
                    onChange={(e) => changeLanguage(e)}
                  />
                </div>
                <div style={{marginTop: '11px'}}>
                  <Typography sx={{color: darkMode ? 'white' : 'black'}}>{t('languages.en')}</Typography>
                </div>

                <Box sx={{width: '1px', height: '24px', backgroundColor: darkMode ? '#555555' : '#CCCCCC', mx: 1}}/>

                {/* Dark mode switch */}
                <LightModeRounded sx={{color: darkMode ? 'grey.500' : 'warning.main'}}/>
                <Switch
                  checked={darkMode}
                  onChange={toggleDarkMode}
                />
                <DarkModeRounded sx={{color: darkMode ? 'primary.light' : 'grey.500'}}/>

                <IconButton sx={{ml: 1}} size='large' onClick={async () => {
                  await KeycloakService.instance.doLogout();
                }}>
                  <LogoutRounded/>
                </IconButton>
              </Stack>
            </Stack>
            : null}
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        open={isLoggedIn && sidebarOpen}
        variant="persistent"
        slotProps={{
          paper: {
            ref: drawerPaperRef,
            sx: {
              width: 260,
              top: {xs: 56, sm: 64},
              height: {xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)'},
              position: 'fixed',
              zIndex: (t) => t.zIndex.appBar - 1,
              boxShadow: 3,
            },
          },
        }}
      >
        <Box role="navigation" sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
          <List>
            {sidebarNavItems.map((item) => (
              <ListItem disablePadding key={item.key}>
                <ListItemButton
                  component="a"
                  href={item.path}
                  selected={isCurrentPath(item.path)}
                >
                  <ListItemText
                    primary={t(`appBar.${item.key}`)}
                    slotProps={{
                      primary: {
                        sx: {
                          color: isCurrentPath(item.path) ? theme.palette.error.main : theme.palette.text.primary,
                        },
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {isLoggedIn ?
        <div style={{width: "100%", display: "block"}}>
          <ErrorBoundary>
          <Routes>
            <Route path="/" element={
              isAdmin
                ? <Navigate to="/customers" replace/>
                : isOnlyViewer
                  ? <Navigate to="/view/customers" replace/>
                  : <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", height: "100vh"}}>
                    <Typography variant="h6" color="text.secondary">
                      {t('noAccess')}
                    </Typography>
                  </Box>
            }/>

            {/* Viewer-only routes */}
            <Route path="/view/customers" element={isOnlyViewer ? <ViewerCustomerPage/> : <Navigate to="/" replace/>}/>
            <Route path="/view/products/:type" element={isOnlyViewer ? <ViewerProductPage/> : <Navigate to="/" replace/>}/>

            {/* Admin routes */}
            <Route path="/customers" element={isAdmin ? <CustomerPage/> : <Navigate to="/" replace/>}/>
            <Route path="/customers/add" element={isAdmin ? <UpdateCustomerPage/> : <Navigate to="/" replace/>}/>
            <Route path="/customers/update/:id" element={isAdmin ? <UpdateCustomerPage/> : <Navigate to="/" replace/>}/>
            <Route path="/salesperson" element={isAdmin ? <SalesPersonPage/> : <Navigate to="/" replace/>}/>
            <Route path="/salesperson/add" element={isAdmin ? <UpdateSalesPersonPage/> : <Navigate to="/" replace/>}/>
            <Route path="/salesperson/update/:id" element={isAdmin ? <UpdateSalesPersonPage/> : <Navigate to="/" replace/>}/>
            <Route path="/products/inCareNetHF" element={isAdmin ? <ProductPage productType={'inCareNetHF'}/> : <Navigate to="/" replace/>}/>
            <Route path="/products/netECG" element={isAdmin ? <ProductPage productType={'netECG'}/> : <Navigate to="/" replace/>}/>
            <Route path="/products/inPace" element={isAdmin ? <ProductPage productType={'inPace'}/> : <Navigate to="/" replace/>}/>
            <Route path="/products/add" element={isAdmin ? <AddProductPage title={"Produkt hinzufügen"}/> : <Navigate to="/" replace/>}/>

            {/* Admin only routes */}
            <Route path="/report" element={isAdmin ? <ReportPage/> : <Navigate to="/" replace/>}/>
            <Route path="/configuration" element={isAdmin ? <ConfigPage/> : <Navigate to="/" replace/>}/>
            <Route path="/billing" element={isSuperAdmin ? <BillingPage/> : <Navigate to="/" replace/>}/>
            <Route path="/auditlog" element={isAdmin ? <AuditLogPage/> : <Navigate to="/" replace/>}/>

            {/* All-role routes */}
            <Route path="/manual" element={<Suspense fallback={<CircularProgress/>}><ManualPage/></Suspense>}/>
          </Routes>
          </ErrorBoundary>
        </div> :
        <Welcome/>
      }
    </>
  );
}
